/**
 * Reusable country selector — shared behaviour for every email signup form.
 *
 * Exposes `window.SidekickCountry` with:
 *   - init(rootEl)        wire up a single [data-country-selector] root
 *   - initAll(scope)      wire up every [data-country-selector] in a scope
 *   - getValue(rootEl)    read the resolved country (predefined OR Other text)
 *   - validate(rootEl)    paint invalid state + return { ok, error }
 *
 * Each form's submit handler calls `SidekickCountry.validate(form)` to gate
 * submission and `SidekickCountry.getValue(form)` to pull the value for the
 * Klaviyo payload. `rootEl` can be the selector root itself OR any ancestor
 * that contains it — handy because some forms wrap the selector inside an
 * email field group.
 *
 * Auto-inits on DOMContentLoaded and re-inits on shopify:section:load so the
 * selector works in the theme editor without page reload.
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.csInit === '1') return;
    root.dataset.csInit = '1';

    var select = root.querySelector('[data-country-select]');
    var otherInput = root.querySelector('[data-country-other]');
    if (!select || !otherInput) return;

    function syncOtherVisibility() {
      var isOther = select.value === 'Other';
      otherInput.hidden = !isOther;
      otherInput.setAttribute('aria-hidden', isOther ? 'false' : 'true');
      if (isOther) {
        otherInput.setAttribute('required', 'true');
      } else {
        otherInput.removeAttribute('required');
        otherInput.value = '';
        otherInput.classList.remove('is-invalid');
        otherInput.removeAttribute('aria-invalid');
      }
    }

    select.addEventListener('change', function () {
      select.classList.remove('is-invalid');
      select.removeAttribute('aria-invalid');
      syncOtherVisibility();
    });

    otherInput.addEventListener('input', function () {
      otherInput.classList.remove('is-invalid');
      otherInput.removeAttribute('aria-invalid');
    });

    syncOtherVisibility();
  }

  function resolveRoot(rootOrAncestor) {
    if (!rootOrAncestor) return null;
    if (typeof rootOrAncestor.matches === 'function' && rootOrAncestor.matches('[data-country-selector]')) {
      return rootOrAncestor;
    }
    if (typeof rootOrAncestor.querySelector === 'function') {
      return rootOrAncestor.querySelector('[data-country-selector]');
    }
    return null;
  }

  function getValue(rootOrAncestor) {
    var root = resolveRoot(rootOrAncestor);
    if (!root) return '';
    var select = root.querySelector('[data-country-select]');
    var otherInput = root.querySelector('[data-country-other]');
    if (!select) return '';
    var v = (select.value || '').trim();
    if (v === 'Other') {
      return ((otherInput && otherInput.value) || '').trim();
    }
    return v;
  }

  function validate(rootOrAncestor) {
    var root = resolveRoot(rootOrAncestor);
    if (!root) return { ok: true, error: '' };

    var select = root.querySelector('[data-country-select]');
    var otherInput = root.querySelector('[data-country-other]');
    var v = (select && select.value || '').trim();

    if (!v) {
      if (select) {
        select.classList.add('is-invalid');
        select.setAttribute('aria-invalid', 'true');
        if (typeof select.focus === 'function') select.focus();
      }
      return { ok: false, error: 'Please select your country.' };
    }

    if (v === 'Other') {
      var other = ((otherInput && otherInput.value) || '').trim();
      if (!other) {
        if (otherInput) {
          otherInput.classList.add('is-invalid');
          otherInput.setAttribute('aria-invalid', 'true');
          if (typeof otherInput.focus === 'function') otherInput.focus();
        }
        return { ok: false, error: 'Please type your country.' };
      }
    }

    if (select) {
      select.classList.remove('is-invalid');
      select.removeAttribute('aria-invalid');
    }
    if (otherInput) {
      otherInput.classList.remove('is-invalid');
      otherInput.removeAttribute('aria-invalid');
    }
    return { ok: true, error: '' };
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-country-selector]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  window.SidekickCountry = {
    init: init,
    initAll: initAll,
    getValue: getValue,
    validate: validate
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () { initAll(); });
  document.addEventListener('shopify:block:select', function () { initAll(); });
})();
